import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { MapPin } from "lucide-react";

/**
 * Loading transition overlay. Fires on every in-app route change,
 * showing a stylised map with an animated locator pin that pulses
 * while the next page hydrates. Auto-dismisses after ~700ms.
 */
const DISMISS_MS = 850;

const PageLocatorOverlay = () => {
  const { pathname, key } = useLocation();
  const [open, setOpen] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    // Skip the initial mount — we only animate between pages, not on the very
    // first load (which already has its own hero animations).
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setOpen(true);
    const t = setTimeout(() => setOpen(false), DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, pathname]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="locator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="page-locator-overlay"
          className="pointer-events-none fixed inset-0 z-[150] flex items-center justify-center"
        >
          {/* Backdrop — slight blur, keeps the page visible underneath */}
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm" />

          {/* Map tile with sweeping pin */}
          <motion.div
            initial={{ scale: 0.9, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 6 }}
            transition={{ type: "spring", stiffness: 240, damping: 20 }}
            className="relative flex flex-col items-center"
          >
            <div className="relative h-28 w-44 overflow-hidden rounded-2xl shadow-lift sm:h-32 sm:w-52">
              {/* Map backdrop */}
              <svg viewBox="0 0 160 90" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                <defs>
                  <linearGradient id="loc-bg" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0" stopColor="#F6EBD1" />
                    <stop offset="1" stopColor="#EFE4CB" />
                  </linearGradient>
                </defs>
                <rect width="160" height="90" fill="url(#loc-bg)" />
                {Array.from({ length: 10 }).map((_, i) => (
                  <path key={`h-${i}`} d={`M0 ${i * 9} H160`} stroke="#E8DFCF" strokeWidth="0.3" />
                ))}
                {Array.from({ length: 18 }).map((_, i) => (
                  <path key={`v-${i}`} d={`M${i * 9} 0 V90`} stroke="#E8DFCF" strokeWidth="0.3" />
                ))}
                <ellipse cx="30" cy="30" rx="12" ry="7" fill="#DCE9D9" opacity="0.7" />
                <ellipse cx="110" cy="60" rx="10" ry="6" fill="#CDE4E4" opacity="0.6" />

                {/* Sweep path for the pin */}
                <motion.circle
                  cx={0}
                  cy={0}
                  r="2.5"
                  fill="var(--brand-accent)"
                  initial={{ cx: 15, cy: 70 }}
                  animate={{ cx: 140, cy: 25 }}
                  transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                />
                {/* Dotted route the pin travels */}
                <motion.path
                  d="M 15 70 Q 50 50 80 55 Q 115 60 140 25"
                  fill="none"
                  stroke="var(--brand-accent)"
                  strokeWidth="0.8"
                  strokeDasharray="1.4 1.6"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0.4 }}
                  animate={{ pathLength: 1, opacity: 0.9 }}
                  transition={{ duration: 0.6 }}
                />
              </svg>

              {/* Floating pin element (actual visible pin) */}
              <motion.div
                initial={{ left: "9%", top: "72%", opacity: 0 }}
                animate={{ left: "87%", top: "20%", opacity: 1 }}
                transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                className="absolute flex -translate-x-1/2 -translate-y-full flex-col items-center"
              >
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-full text-white shadow-glow"
                  style={{ background: "var(--brand-accent)" }}
                >
                  <MapPin className="h-3.5 w-3.5" strokeWidth={2.6} fill="currentColor" fillOpacity="0.25" />
                </div>
                <span
                  className="-mt-1 h-1.5 w-1.5 rotate-45"
                  style={{ background: "var(--brand-accent)" }}
                />
                {/* Pulsing landing ring */}
                <motion.span
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: [0, 0.5, 0], scale: [0.4, 2.4, 2.8] }}
                  transition={{ delay: 0.55, duration: 0.65 }}
                  className="absolute left-1/2 top-[14px] -z-10 h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ background: "var(--brand-accent)" }}
                />
              </motion.div>
            </div>

            {/* Caption */}
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-4 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-tracksy-ink"
            >
              <span
                className="h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ background: "var(--brand-accent)" }}
              />
              Locating next page
              <span className="inline-flex gap-0.5" aria-hidden="true">
                <motion.span animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.1, repeat: Infinity, delay: 0.0 }}>.</motion.span>
                <motion.span animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.1, repeat: Infinity, delay: 0.2 }}>.</motion.span>
                <motion.span animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.1, repeat: Infinity, delay: 0.4 }}>.</motion.span>
              </span>
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageLocatorOverlay;
